import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  Inject,
  inject,
  OnInit,
  Optional,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  FwAlertModule,
  FwAvatarModule,
  FwButtonModule,
  FwDateInputModule,
  FwDialogsModule,
  FwFormHeadingModule,
  FwIconModule,
  FwLayoutsModule,
  FwMenuItemComponent,
  FwProgressModule,
  FwSelectMenuModule,
  FwTextAreaInputModule,
  FwTextInputModule, FwTypeaheadComponent,
  FwWrappedInputModule,
  IconType,
} from '@flywheel-io/vision';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import { CoreApiService, queryAll } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { MaterialModule } from '../../material.module';
import { NotificationService } from '../../notification.service';
import { ProjectsService } from '../../projects/projects.service';
import { ExpansionPanelComponent } from '../../shared/components/expansion-panel/expansion-panel.component';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import {
  ReaderTaskContainerType,
  TaskAssigneeType,
  TaskPriority,
  TaskStatus,
  TaskType,
} from '../../shared/enums/task.enum';
import { Option } from '../../shared/models/option.model';
import { Project } from '../../shared/models/project.model';
import { Protocol, ProtocolStatus } from '../../shared/models/protocol.model';
import { SearchFilter, SearchQuery, SearchReturnType } from '../../shared/models/search/search-query.model';
import { SearchResultResponse } from '../../shared/models/search/search-results.model';
import { StaffingPool } from '../../shared/models/staffing-pool.model';
import { BatchReaderTaskCreate, ReaderTaskCreate, TaskAssignee } from '../../shared/models/task.model';
import { User } from '../../shared/models/user.model';
import { PipesModule } from '../../shared/pipes.module';
import { SharedModule } from '../../shared/shared.module';
import { TaskManagerService } from '../task-manager.service';

interface CreateTaskFormValue {
  selectTaskLocation: {
    project: Project & { icon: IconType };
    readProtocol: Protocol['_id'];
    container: {
      type: ReaderTaskContainerType;
      includesTags: string[];
      excludeTags: string[];
    };
  };
  taskDetail: {
    description: string;
    dueDate: string;
    priority: TaskPriority;
    taskTags: string[];
  };
  assignTask: {
    assignee: TaskAssignee;
  };
}

const jobPrioritiesOrder: TaskPriority[] = [TaskPriority.Low, TaskPriority.Medium, TaskPriority.High, TaskPriority.Blocker];
const containerTypeOrder: ReaderTaskContainerType[] = [ReaderTaskContainerType.Session, ReaderTaskContainerType.Acquisition, ReaderTaskContainerType.File];
const mapOption = (value: string): Option => ({ value });

const getBatchCreatePayload = (formValue: CreateTaskFormValue): BatchReaderTaskCreate => {
  const container = formValue.selectTaskLocation.container;

  const payload: BatchReaderTaskCreate = {
    protocol_id: formValue.selectTaskLocation.readProtocol,
    type: TaskType.Reader,
    subcategory: TaskType.Reader,
    project_id: formValue.selectTaskLocation.project._id,
    due_date: formValue.taskDetail.dueDate,
    status: TaskStatus.Todo,
    priority: formValue.taskDetail.priority,
    filters: {
      include_tags: container.includesTags,
      exclude_tags: container.excludeTags,
      container_type: container.type,
    },
  };

  const assignee = formValue.assignTask.assignee;
  const taskTags = formValue.taskDetail.taskTags;
  const description = formValue.taskDetail.description;

  if (assignee) {
    payload.assignee = assignee;
  }

  if (taskTags?.length) {
    payload.tags = taskTags;
  }

  if (description) {
    payload.description = description;
  }

  return payload;
};

const getTaskCreatePayload = (
  formValue: CreateTaskFormValue,
  containerId: string,
  containerType: ReaderTaskContainerType
): ReaderTaskCreate => {
  const payload: ReaderTaskCreate = {
    protocol_id: formValue.selectTaskLocation.readProtocol,
    type: TaskType.Reader,
    subcategory: TaskType.Reader,
    status: TaskStatus.Todo,
    priority: formValue.taskDetail.priority,
    due_date: formValue.taskDetail.dueDate,
    description: formValue.taskDetail.description,
    parent_ref: {
      id: containerId,
      type: containerType,
    },
  };

  const assignee = formValue.assignTask.assignee;
  const taskTags = formValue.taskDetail.taskTags;

  if (assignee) {
    payload.assignee = assignee;
  }

  if (taskTags?.length) {
    payload.tags = taskTags;
  }

  return payload;
};

@Component({
  selector: 'create-reader-task-dialog',
  standalone: true,
  host: {
    'test-id': 'create-reader-task-dialog',
  },
  imports: [
    CommonModule,
    ExpansionPanelComponent,
    FwAlertModule,
    FwAvatarModule,
    FwButtonModule,
    FwDateInputModule,
    FwDialogsModule,
    FwFormHeadingModule,
    FwIconModule,
    FwLayoutsModule,
    FwProgressModule,
    FwSelectMenuModule,
    FwTextAreaInputModule,
    FwTextInputModule,
    FwTypeaheadComponent,
    FwWrappedInputModule,
    MaterialModule,
    PipesModule,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './create-reader-task-dialog.component.html',
  styleUrls: ['./create-reader-task-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateReaderTaskDialogComponent implements OnInit {

  protected priorities = jobPrioritiesOrder.map(mapOption);
  protected containerTypes = containerTypeOrder.map(mapOption);
  protected prioritiesIcons: Record<TaskPriority, IconType> = {
    [TaskPriority.Blocker]: 'close-circled',
    [TaskPriority.High]: 'arrow-up-high-priority',
    [TaskPriority.Medium]: 'menu-burger-handle',
    [TaskPriority.Low]: 'arrow-up-low-priority',
  };
  protected prioritiesIconColors: Record<TaskPriority, FwMenuItemComponent['iconColor']> = {
    [TaskPriority.Blocker]: 'red',
    [TaskPriority.High]: 'red',
    [TaskPriority.Medium]: 'orange',
    [TaskPriority.Low]: 'primary',
  };
  protected taskPrioritiesLabels: Record<TaskPriority, string> = {
    [TaskPriority.Blocker]: 'Blocker',
    [TaskPriority.High]: 'High',
    [TaskPriority.Medium]: 'Medium',
    [TaskPriority.Low]: 'Low',
  };
  protected containerTypeLabel: Record<ReaderTaskContainerType, string> = {
    [ReaderTaskContainerType.Session]: 'Session',
    [ReaderTaskContainerType.Acquisition]: 'Acquisition',
    [ReaderTaskContainerType.File]: 'File',
  };
  protected users$: Observable<User[]> = of();
  protected filteredUsers$: Observable<User[]> = of();
  protected projects$: Observable<(Project & { icon: IconType, disabled?: boolean })[]> = of([]);
  protected protocols$: Observable<Protocol[]> = of();
  protected containerTags$: Observable<string[]> = of();
  protected staffingPools$: Observable<StaffingPool[]> = of();
  protected containersCount$: Observable<number> = of();
  protected isLoadingContainers = signal(false);
  protected isCreating = signal(false);

  private projectControl$ = new ReplaySubject<Project & { icon: IconType }>(1);
  private containerTypeControl$ = new ReplaySubject<ReaderTaskContainerType>(1);
  protected priorityCSSVarIconColor = computed(() => {
    const color = this.prioritiesIconColors[this.taskPriorityChanges()];
    return `var(--${color}-base)`;
  });

  protected createTaskForm = new FormGroup({
    selectTaskLocation: new FormGroup({
      project: new FormControl<Project & { icon: IconType }>(null, { validators: Validators.required }),
      readProtocol: new FormControl<Protocol['_id']>({ disabled: true, value: null }, { validators: Validators.required }),
      container: new FormGroup({
        type: new FormControl<ReaderTaskContainerType>(null, { validators: Validators.required }),
        includesTags: new FormControl<string[]>([]),
        excludeTags: new FormControl<string[]>([]),
      }),
    }),
    taskDetail: new FormGroup({
      description: new FormControl<string>(null, Validators.maxLength(250)),
      dueDate: new FormControl<string>(null),
      priority: new FormControl<TaskPriority>(TaskPriority.Medium, { validators: Validators.required }),
      taskTags: new FormControl<string[]>([]),
    }),
    assignTask: new FormGroup({
      assignee: new FormControl<TaskAssignee>({ disabled: true, value: null }, { validators: Validators.required }),
    }),
  });

  private taskPriorityChanges = toSignal(this.createTaskForm.get('taskDetail.priority').valueChanges.pipe(startWith(this.createTaskForm.get('taskDetail.priority').value)));
  private destroyRef = inject(DestroyRef);
  protected taskTags = this.taskManagerService.getReaderTaskTags();

  constructor(
    @Inject(DIALOG_DATA) @Optional() protected dialogData: {projectId?: Project['_id'], containerType?: ReaderTaskContainerType, containerId?: string},
    protected dialogRef: DialogRef,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private projectsService: ProjectsService,
    private taskManagerService: TaskManagerService,
  ) {
  }

  ngOnInit(): void {
    this.setupProjectsData();
    this.setupUsersData();
    this.setupStaffingPoolsData();
    this.setupFormSubscriptions();
    this.prefillFormFromDialogData();
  }

  private setupProjectsData(): void {
    this.projects$ = combineLatest([
      this.projectsService.getProjects(),
      this.projectsService.getProjectsWithPermissions([PermissionAction.TasksModify]),
    ]).pipe(
      map(([projects, tasksModifyProjects]) => {
        const hasModifyTasksPermission: Record<string, boolean> = {};
        for (const tasksModifyProject of tasksModifyProjects) {
          hasModifyTasksPermission[tasksModifyProject._id] = true;
        }
        return projects.sort((a, b) => a.label > b.label ? 1 : -1).map(project => ({
          ...project,
          icon: 'folder-open' as IconType,
          disabled: !hasModifyTasksPermission[project._id],
        }));
      }),
    );
  }

  private setupUsersData(): void {
    this.users$ = this.projectsService.getUsers();

    // Setup filteredUsers$ with ReplaySubject to handle both sync and async project changes
    this.filteredUsers$ = combineLatest([
      this.users$,
      this.projectControl$.pipe(startWith(null)),
    ]).pipe(
      map(([users, project]) => {
        if (!project) {
          return null;
        }
        const projectPermissionsIds = new Set(project?.permissions.map(p => p._id));
        return users
          .filter(user => projectPermissionsIds.has(user._id))
          .sort((a, b) => {
            const nameA = `${a.firstname} ${a.lastname}`.toLowerCase();
            const nameB = `${b.firstname} ${b.lastname}`.toLowerCase();
            return nameA.localeCompare(nameB);
          });
      }),
      shareReplay(1),
    );
  }

  private setupStaffingPoolsData(): void {
    this.staffingPools$ = queryAll(this.coreApi.staffingPools.query, { sort: 'label:asc' });
  }

  private setupFormSubscriptions(): void {
    this.subscribeOnFormChanges();
  }

  private prefillFormFromDialogData(): void {
    const projectControl = this.createTaskForm.get('selectTaskLocation.project');

    // Pre-populate project if provided
    if (this.dialogData?.projectId) {
      this.projects$.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(projects => {
        const project = projects.find(project => project._id === this.dialogData.projectId);
        if (project) {
          projectControl.setValue(project);
          this.projectControl$.next(project);
        }
      });
    }

    // Pre-populate container type if provided
    if (this.dialogData?.containerType) {
      this.createTaskForm.get('selectTaskLocation.container.type').setValue(this.dialogData.containerType);
      this.containerTypeControl$.next(this.dialogData.containerType);
    }
  }

  protected subscribeOnFormChanges(): void {
    const projectControl = this.createTaskForm.get('selectTaskLocation.project');
    const containerTypeControl = this.createTaskForm.get('selectTaskLocation.container.type');
    const containerIncludesTagsControl = this.createTaskForm.get('selectTaskLocation.container.includesTags');
    const containerExcludeTagsControl = this.createTaskForm.get('selectTaskLocation.container.excludeTags');

    // Initialize containerTags$ based on project
    this.containerTags$ = this.projectControl$.pipe(
      switchMap((project) => {
        if (!project) {
          return of(null);
        }
        return this.projectsService.getGroups().pipe(
          map((groups) => groups?.find(group => group._id === project?.group)?.tags || []),
        );
      }),
    );

    // Initialize containersCount$ - always needed regardless of dialogData
    this.containersCount$ = combineLatest([
      this.projectControl$.pipe(startWith(null)),
      this.containerTypeControl$.pipe(startWith(null)),
      containerIncludesTagsControl.valueChanges.pipe(startWith(containerIncludesTagsControl.value)),
      containerExcludeTagsControl.valueChanges.pipe(startWith(containerExcludeTagsControl.value)),
    ]).pipe(
      tap(() => {
        this.isLoadingContainers.set(true);
      }),
      filter(([project, containerType]) => Boolean(project) && Boolean(containerType)),
      switchMap(([project, containerType, includesTags, excludeTags]) =>
        this.getContainersCount(containerType, project._id, includesTags, excludeTags).pipe(
          finalize(() => {
            this.isLoadingContainers.set(false);
          }),
        ),
      ),
      shareReplay(1),
    );

    this.protocols$ = this.projectControl$.pipe(
      startWith(null),
      switchMap((project) => project
        ? queryAll(this.coreApi.protocols.query, {
            filter: `parent_ref.id=${project.group},parent_ref.type=${ContainerType.Group},status=${ProtocolStatus.Published}`,
            sort: 'label:asc',
          })
        : of(undefined)
      ),
      tap((protocols: Protocol[]) => {
        const protocolControl = this.createTaskForm.get('selectTaskLocation.readProtocol');
        if (projectControl.valid && protocols?.length) {
          protocolControl.enable();
          if (protocolControl.value && !protocols.some(protocol => protocol._id === protocolControl.value)) {
            protocolControl.reset();
          }
        } else {
          protocolControl.disable();
          protocolControl.reset();
        }
      }),
    );

    // handle container tags controls
    this.containerTags$.pipe(
      startWith([]),
      tap((tags: { value: string }[]) => {
        const includesTagsControl = this.createTaskForm.get('selectTaskLocation.container.includesTags');
        const excludeTagsControl = this.createTaskForm.get('selectTaskLocation.container.excludeTags');
        const tagsList = tags.map(tag => tag.value);
        if (projectControl.valid && tagsList.length) {
          includesTagsControl.enable();
          excludeTagsControl.enable();
          includesTagsControl.patchValue(includesTagsControl.value.filter(tag => tagsList.includes(tag)), { emitEvent: false });
          excludeTagsControl.patchValue(excludeTagsControl.value.filter(tag => tagsList.includes(tag)), { emitEvent: false });
        } else {
          includesTagsControl.disable();
          excludeTagsControl.disable();
          includesTagsControl.reset([]);
          excludeTagsControl.reset([]);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    projectControl.valueChanges.pipe(
      tap((project: Project & { icon: IconType }) => {
        const assigneeControl = this.createTaskForm.get('assignTask.assignee');

        if (project) {
          this.projectControl$.next(project);
          assigneeControl.enable();
        } else {
          assigneeControl.disable();
        }
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    containerTypeControl.valueChanges.pipe(
      tap((containerType) => {
        if (containerType) {
          this.containerTypeControl$.next(containerType);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  protected createTask(): void {
    this.isCreating.set(true);

    const formValue = this.createTaskForm.value as CreateTaskFormValue;
    const taskTags = formValue.taskDetail.taskTags;
    const hasSingleContainer = this.dialogData?.containerId && this.dialogData?.containerType;

    const createRequest$ = hasSingleContainer
      ? this.coreApi.tasks.reader.post(getTaskCreatePayload(formValue, this.dialogData.containerId, this.dialogData.containerType))
      : this.coreApi.tasks.reader.batch(getBatchCreatePayload(formValue));

    createRequest$.pipe(
      tap(() => {
        // Save task tags to localStorage
        if (taskTags?.length) {
          this.taskManagerService.addReaderTaskTags(taskTags);
        }
        this.notifications.success('tasks.create.success');
        this.dialogRef.close(true);
      }),
      catchError(error => {
        this.logger.error(error);
        this.notifications.error('tasks.create.error');
        return of(null);
      }),
      finalize(() => {
        this.isCreating.set(false);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  protected onAssigneeChanged(id: string, staffingPools: StaffingPool[]): void {
    const isStaffingPool = staffingPools.some(pool => pool._id === id);
    this.createTaskForm.get('assignTask.assignee').setValue({
      id,
      type: isStaffingPool ? TaskAssigneeType.StaffingPool : TaskAssigneeType.User,
    });
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  private getContainersCount(
    containerType: ReaderTaskContainerType,
    projectId: string,
    includesTags: string[] = [],
    excludeTags: string[] = []
  ): Observable<number> {
    const buildSearchQuery = (): SearchQuery => {
      const returnTypeMap: Record<ReaderTaskContainerType, SearchReturnType> = {
        [ReaderTaskContainerType.Acquisition]: SearchReturnType.Acquisition,
        [ReaderTaskContainerType.File]: SearchReturnType.File,
        [ReaderTaskContainerType.Session]: SearchReturnType.Session,
      };

      const containerTypeFieldMap: Record<ReaderTaskContainerType, string> = {
        [ReaderTaskContainerType.Acquisition]: 'acquisition.tags',
        [ReaderTaskContainerType.File]: 'file.tags',
        [ReaderTaskContainerType.Session]: 'session.tags',
      };

      const tagsField = containerTypeFieldMap[containerType] || containerTypeFieldMap[ReaderTaskContainerType.Session];
      const filters: (SearchFilter | { bool: { must_not: { term: { [key: string]: string } }[] } })[] = [];

      // Add include tags filter (container must have at least one of these tags)
      if (includesTags?.length > 0) {
        filters.push({
          terms: { [tagsField]: includesTags },
        });
      }

      // Add exclude tags filter (container must not have any of these tags)
      if (excludeTags?.length > 0) {
        filters.push({
          bool: {
            must_not: excludeTags.map(tag => ({
              term: { [tagsField]: tag },
            })),
          },
        });
      }

      return {
        filters: filters as SearchFilter[],
        return_type: returnTypeMap[containerType] || SearchReturnType.Session,
        all_data: false,
        structured_query: `project._id = ${projectId}${containerType === ReaderTaskContainerType.File ? ' AND parent.type = acquisition' : ''}`,
      };
    }

    const getContainersCount = (response: SearchResultResponse): number => response.results?.length || 0;

    return this.coreApi.dataexplorer.search({}, buildSearchQuery()).pipe(map(getContainersCount));
  }
}
